// HÓNAP-SEGÉDFÜGGVÉNYEK (`YYYY-MM`) — TISZTA, determinisztikus (könnyen tesztelhető).
//
// MIÉRT `lib/` ÉS NEM EGY FEATURE ALATT: a modult ma HAT felület hívja (Pénzügy · Számlák · Használati adatok ·
// kiadás-cellák · tömeges besorolás · a közös `MonthStepper`), tehát a réteg-szabály szerint a közös rétegbe
// tartozik. Korábban a `features/revenue-forecast/` alatt élt, és emiatt a `components/shared/month-stepper.tsx`
// egy FEATURE-mappából importált — pont az, amit az atom-rétegnek tilos (a `components/ui`/`shared` sosem
// feature-tudatos).
// A szerződés-formátum `YYYY-MM`; a léptetés/címke ebből dolgozik. A hónap-határ üzleti naptára szerver-oldalon
// Europe/Budapest; itt csak a UI-navigáció (léptetés + megjelenített felirat) él, tz-független karakter-aritmetikával.

/** Az aktuális naptári hónap `YYYY-MM` alakban (a helyi óra szerint — a picker kezdőértéke). */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** `YYYY-MM` eltolása `delta` hónappal (előjeles). Érvénytelen bemenet → változatlanul visszaadva. */
export function shiftMonth(month: string, delta: number): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!m) return month;
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1; // 0-alapú
  const total = year * 12 + monthIndex + delta;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12; // 0..11 (Math.floor osztás → mindig nem-negatív maradék)
  return `${ny}-${String(nm + 1).padStart(2, '0')}`;
}

/**
 * `YYYY-MM` → RÖVIDÍTETT, locale-tudatos felirat: „2026. szept." (hu) / „Sep 2026" (en). Érvénytelen → nyers.
 *
 * MIÉRT KELL A RÖVID ALAK: a hónap-léptető VEZÉRLŐ, nem tartalom — a teljes hónapnév („2026. szeptember")
 * miatt a sáv elején ülő léptető aránytalanul széles volt. A LISTÁKBAN és a legördülőkben marad a teljes
 * alak: ott a hónap olvasandó adat, nem kattintandó gomb.
 */
export function formatMonthLabelShort(month: string, locale: string): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!m) return month;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  const loc = locale.toLowerCase().startsWith('en') ? 'en-GB' : 'hu-HU';
  return new Intl.DateTimeFormat(loc, { year: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    date,
  );
}

/**
 * `YYYY-MM` → CSAK a rövid hónapnév („szept." / „Sep") — a 12 hónapos chartok tengelycímkéje, ahol az év a
 * tengely egészéből olvasható, és a teljes alak a sűrű tengelyen egymásba futna.
 */
export function formatMonthAxisLabel(month: string, locale: string): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!m) return month;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  const loc = locale.toLowerCase().startsWith('en') ? 'en-GB' : 'hu-HU';
  return new Intl.DateTimeFormat(loc, { month: 'short', timeZone: 'UTC' }).format(date);
}

/** `YYYY-MM` → locale-tudatos felirat: „2026. augusztus" (hu) / „August 2026" (en). Érvénytelen → a nyers érték. */
export function formatMonthLabel(month: string, locale: string): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!m) return month;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  const loc = locale.toLowerCase().startsWith('en') ? 'en-GB' : 'hu-HU';
  return new Intl.DateTimeFormat(loc, { year: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    date,
  );
}

/**
 * `YYYY-MM` → a hónap ZÁRT nap-intervalluma (`from` = 1-je, `to` = utolsó nap), `YYYY-MM-DD` alakban.
 *
 * MIÉRT KELL: a lista-végpontok nap-pontos tartományt szűrnek (`issuedFrom`/`issuedTo`), a lapok viszont
 * HÓNAP-dimenzióban gondolkodnak (a hónap-léptető). A két alak közti fordítás EGY helyen él, különben a
 * hónap utolsó napja könnyen kimarad. Érvénytelen bemenet → üres tartomány (a hívó ilyenkor nem szűr).
 */
export function monthRange(month: string): { from: string; to: string } {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!m) return { from: '', to: '' };
  // `Date.UTC(y, mIndex + 1, 0)` = az ELŐZŐ hónap utolsó napja, azaz a kért hónap utolsó napja.
  const last = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}
